import React, { useState, useRef } from 'react';
import HCaptcha from '@hcaptcha/react-hcaptcha';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import { useToast } from '../hooks/use-toast';
import { Send, Mail, Phone, MapPin } from 'lucide-react';

const ContactForm = () => {
  const { toast } = useToast();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [captchaToken, setCaptchaToken] = useState(null);
  const captchaRef = useRef(null);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleCaptchaVerify = (token) => {
    setCaptchaToken(token);
  };

  const handleCaptchaExpire = () => {
    setCaptchaToken(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Check if captcha is verified
    if (!captchaToken) {
      toast({
        title: "CAPTCHA erforderlich",
        description: "Bitte bestätigen Sie, dass Sie kein Roboter sind.",
        variant: "destructive"
      });
      return;
    }

    setIsSubmitting(true);

    try {
      // PHP Backend URL
      const response = await fetch('/api/contact.php', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          ...formData,
          captchaToken: captchaToken
        })
      });

      const data = await response.json();

      if (response.ok && data.success) {
        toast({
          title: "✅ Nachricht gesendet!",
          description: data.message || "Vielen Dank für Ihre Anfrage. Wir melden uns in Kürze bei Ihnen.",
        });
        
        // Reset form and captcha
        setFormData({
          name: '',
          email: '',
          phone: '',
          company: '',
          message: ''
        });
        setCaptchaToken(null);
        if (captchaRef.current) {
          captchaRef.current.resetCaptcha();
        }
      } else {
        toast({
          title: "❌ Fehler beim Senden",
          description: data.error || "Es ist ein Fehler aufgetreten. Bitte versuchen Sie es später erneut.",
          variant: "destructive"
        });
      }
    } catch (error) {
      console.error('Error submitting form:', error);
      toast({
        title: "❌ Verbindungsfehler",
        description: "Die Nachricht konnte nicht gesendet werden. Bitte prüfen Sie Ihre Internetverbindung.",
        variant: "destructive"
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="grid lg:grid-cols-3 gap-8">
      {/* Contact Info */}
      <div className="space-y-6">
        <Card className="group hover:shadow-lg transition-all hover:border-orange-200">
          <CardHeader>
            <div className="w-12 h-12 bg-gradient-to-br from-orange-100 to-red-100 rounded-lg flex items-center justify-center mb-3 group-hover:scale-110 transition-transform">
              <Mail className="text-orange-500" size={24} />
            </div>
            <CardTitle className="text-lg">E-Mail</CardTitle>
            <CardDescription>
              <a href="mailto:info@apppilot.de" className="text-orange-500 hover:text-orange-600 font-medium">
                info@apppilot.de
              </a>
            </CardDescription>
          </CardHeader>
        </Card>

        <Card className="group hover:shadow-lg transition-all hover:border-orange-200">
          <CardHeader>
            <div className="w-12 h-12 bg-gradient-to-br from-orange-100 to-red-100 rounded-lg flex items-center justify-center mb-3 group-hover:scale-110 transition-transform">
              <Phone className="text-orange-500" size={24} />
            </div>
            <CardTitle className="text-lg">Telefon</CardTitle>
            <CardDescription>
              <a href="tel:+491727785888" className="text-orange-500 hover:text-orange-600 font-medium">
                +49 (0) 172 77 85 888
              </a>
            </CardDescription>
          </CardHeader>
        </Card>

        <Card className="group hover:shadow-lg transition-all hover:border-orange-200">
          <CardHeader>
            <div className="w-12 h-12 bg-gradient-to-br from-orange-100 to-red-100 rounded-lg flex items-center justify-center mb-3 group-hover:scale-110 transition-transform">
              <MapPin className="text-orange-500" size={24} />
            </div>
            <CardTitle className="text-lg">Standort</CardTitle>
            <CardDescription className="text-gray-600">
              Deutschland
              <br />
              <span className="text-sm">Alle Server in DE</span>
            </CardDescription>
          </CardHeader>
        </Card>
      </div>

      {/* Contact Form */}
      <Card className="lg:col-span-2 shadow-xl hover:shadow-2xl transition-shadow">
        <CardHeader>
          <CardTitle className="text-2xl">Senden Sie uns eine Nachricht</CardTitle>
          <CardDescription className="text-base">
            Füllen Sie das Formular aus und wir melden uns schnellstmöglich bei Ihnen.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="grid md:grid-cols-2 gap-6">
              <div className="space-y-2">
                <Label htmlFor="name" className="text-gray-700 font-medium">
                  Name *
                </Label>
                <Input
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  placeholder="Max Mustermann"
                  required
                  className="border-gray-300 focus:border-orange-500 focus:ring-orange-500 transition-colors"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="email" className="text-gray-700 font-medium">
                  E-Mail *
                </Label>
                <Input
                  id="email"
                  name="email"
                  type="email"
                  value={formData.email}
                  onChange={handleChange}
                  placeholder="max@unternehmen.de"
                  required
                  className="border-gray-300 focus:border-orange-500 focus:ring-orange-500 transition-colors"
                />
              </div>
            </div>

            <div className="grid md:grid-cols-2 gap-6">
              <div className="space-y-2">
                <Label htmlFor="phone" className="text-gray-700 font-medium">
                  Telefon
                </Label>
                <Input
                  id="phone"
                  name="phone"
                  type="tel"
                  value={formData.phone}
                  onChange={handleChange}
                  placeholder="+49 123 456789"
                  className="border-gray-300 focus:border-orange-500 focus:ring-orange-500 transition-colors"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="company" className="text-gray-700 font-medium">
                  Unternehmen
                </Label>
                <Input
                  id="company"
                  name="company"
                  value={formData.company}
                  onChange={handleChange}
                  placeholder="Ihr Unternehmen"
                  className="border-gray-300 focus:border-orange-500 focus:ring-orange-500 transition-colors"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="message" className="text-gray-700 font-medium">
                Ihre Nachricht *
              </Label>
              <Textarea
                id="message"
                name="message"
                value={formData.message}
                onChange={handleChange}
                placeholder="Beschreiben Sie Ihr Projekt oder Ihre Anfrage..."
                required
                rows={6}
                className="border-gray-300 focus:border-orange-500 focus:ring-orange-500 transition-colors resize-none"
              />
            </div>

            <div className="space-y-4">
              <div className="flex justify-center">
                <HCaptcha
                  ref={captchaRef}
                  sitekey="800a20f6-2c82-428c-aeee-86643cba7678"
                  onVerify={handleCaptchaVerify}
                  onExpire={handleCaptchaExpire}
                  languageOverride="de"
                />
              </div>

              <div className="flex items-center gap-4">
                <Button
                  type="submit"
                  disabled={isSubmitting || !captchaToken}
                  className="bg-gradient-to-r from-orange-500 to-red-500 hover:from-orange-600 hover:to-red-600 text-white shadow-lg hover:shadow-xl transform hover:scale-105 transition-all flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                  size="lg"
                >
                  {isSubmitting ? (
                    <>
                      <span className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></span>
                      Wird gesendet...
                    </>
                  ) : (
                    <>
                      <Send size={18} />
                      Nachricht senden
                    </>
                  )}
                </Button>
                <p className="text-sm text-gray-500">* Pflichtfelder</p>
              </div>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
};

export default ContactForm;